import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Plus, Trash2, RefreshCw, Send, AlertTriangle, Copy, Wand2, Pencil, BarChart3, Clock } from 'lucide-react';
import api from '../lib/api';
import { WhatsAppTemplateBuilder } from './whatsapp/WhatsAppTemplateBuilder';

const STATUS_COLORS = {
  APPROVED: 'bg-green-500/10 text-green-600 border-green-500/20',
  PENDING: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  REJECTED: 'bg-red-500/10 text-red-600 border-red-500/20',
  PENDING_DELETION: 'bg-red-500/10 text-red-600 border-red-500/20',
  DELETED: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const statusLabel = (s) => {
  const u = (s || '').toUpperCase();
  if (u === 'PENDING_DELETION' || u === 'DELETED') return 'Deleted on Meta';
  return s;
};

const up = (s) => (s || '').toUpperCase();
const getComp = (t, type) => (t?.components || []).find((c) => up(c.type) === type);

// "Created · 12 Aug, 13:49" style timestamp for a template card.
const fmtCreated = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const getBodyVarCount = (t) => {
  const body = getComp(t, 'BODY');
  const matches = (body?.text || '').match(/\{\{\s*(\d+)\s*\}\}/g) || [];
  const idxs = matches.map((m) => parseInt(m.replace(/[^0-9]/g, ''), 10)).filter((n) => !Number.isNaN(n));
  return idxs.length ? Math.max(...idxs) : 0;
};

const FIELD_OPTIONS = [
  { value: 'first_name', label: 'First name' },
  { value: 'full_name', label: 'Full name' },
  { value: 'company', label: 'Company' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'city', label: 'City' },
  { value: 'title', label: 'Job title' },
  { value: 'country', label: 'Country' },
  { value: 'custom', label: 'Custom text' },
];
const FIELD_LABEL = Object.fromEntries(FIELD_OPTIONS.map((o) => [o.value, o.label]));

const inferField = (before) => {
  const b = (before || '').toLowerCase();
  if (/e-?mail|verify/.test(b)) return 'email';
  if (/company|organi[sz]ation|business/.test(b)) return 'company';
  if (/phone|mobile|number|contact/.test(b)) return 'phone';
  if (/city|location/.test(b)) return 'city';
  if (/name/.test(b)) return 'first_name';
  return null;
};

const buildDefaultMap = (t) => {
  const n = getBodyVarCount(t);
  const body = getComp(t, 'BODY')?.text || '';
  const map = [];
  for (let i = 1; i <= n; i++) {
    const idx = body.search(new RegExp(`\\{\\{\\s*${i}\\s*\\}\\}`));
    const before = idx > 0 ? body.slice(Math.max(0, idx - 28), idx) : '';
    const field = inferField(before) || (i === 1 ? 'first_name' : i === 2 ? 'company' : 'custom');
    map.push({ field, value: '' });
  }
  return map;
};

const mapKey = (name) => `wa_tpl_map_${name}`;
const loadSavedMap = (t) => {
  const n = getBodyVarCount(t);
  try {
    const saved = JSON.parse(localStorage.getItem(mapKey(t.name)) || 'null');
    if (Array.isArray(saved) && saved.length === n) return saved;
  } catch { /* ignore */ }
  return buildDefaultMap(t);
};

// Reconstruct a builder prefill from an existing (fetched) template.
const toBuilderInitial = (t, opts = {}) => {
  const body = getComp(t, 'BODY')?.text || '';
  const footer = getComp(t, 'FOOTER')?.text || '';
  const headC = getComp(t, 'HEADER');
  const btnC = getComp(t, 'BUTTONS');
  const matches = (body.match(/\{\{\s*(\d+)\s*\}\}/g) || []);
  const n = matches.length ? Math.max(...matches.map((m) => parseInt(m.replace(/[^0-9]/g, ''), 10))) : 0;

  // P-B — prefill the saved variable_map + examples (the send-time mapping) so an
  // edit never silently resets {{2}}=deal_value back to a default field.
  const savedMap = Array.isArray(t.variable_map) ? t.variable_map : [];
  const savedEx = Array.isArray(t.examples) ? t.examples : [];
  const tplVars = [];
  for (let i = 0; i < n; i++) {
    const sm = savedMap[i] || {};
    const field = sm.field || (i === 0 ? 'first_name' : i === 1 ? 'company' : 'custom');
    const sample = (field === 'custom' ? (sm.value || '') : '') || savedEx[i] || '';
    tplVars.push({ field, sample });
  }

  // P-B — preserve the header. TEXT keeps its text; media headers keep their
  // stored storage_key so an edit/duplicate re-hosts the same image/video/doc
  // instead of dropping it (Meta's edit replaces the whole components array).
  const hfmt = up(headC?.format);
  let header = { format: 'NONE', text: '', sample: '' };
  if (headC && hfmt === 'TEXT') {
    header = { format: 'TEXT', text: headC.text || '', sample: '' };
  } else if (headC && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(hfmt)) {
    const hm = t.header_media || {};
    header = {
      format: hfmt,
      storage_key: hm.storage_key || '',
      filename: hm.filename || '',
      mime: hm.mime || '',
      header_handle: '',
    };
  }

  return {
    form: {
      name: opts.newName || t.name,
      category: t.category || 'MARKETING',
      language: opts.language || t.language || 'en_US',
      body_text: body,
      footer_text: footer,
    },
    header,
    buttons: (btnC?.buttons || []).map((b) => ({
      type: up(b.type), text: b.text || '', url: b.url || '', phone_number: b.phone_number || '', example: '',
    })),
    tplVars,
    rejectedReason: opts.rejectedReason,
  };
};

/**
 * WhatsApp template management panel — list, create (full card builder), delete,
 * duplicate-as-language, fix-and-resubmit rejected, and "Send via API".
 */
export const WhatsAppTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [builderInitial, setBuilderInitial] = useState(null);
  const [leads, setLeads] = useState([]);
  const [sendFor, setSendFor] = useState(null);
  const [sendLeadId, setSendLeadId] = useState('');
  const [sendMap, setSendMap] = useState([]);
  const [sending, setSending] = useState(false);
  const [notConnected, setNotConnected] = useState(false);
  const [statusTab, setStatusTab] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    setNotice('');
    setNotConnected(false);
    try {
      const res = await api.get('/api/social-connectors/whatsapp/templates');
      setTemplates(res.data?.templates || []);
      if (res.data?.detail) setNotice(res.data.detail);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /connect/i.test(detail)) {
        setNotConnected(true);
        setTemplates([]);
      } else {
        toast.error('Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/api/leads/?limit=50').then((r) => setLeads(r.data?.leads || r.data || [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (sendFor && sendMap.length) {
      try { localStorage.setItem(mapKey(sendFor.name), JSON.stringify(sendMap)); } catch { /* ignore */ }
    }
  }, [sendMap, sendFor]);

  const openNew = () => { setBuilderInitial(null); setShowForm(true); };
  const openDuplicate = (t) => {
    setBuilderInitial(toBuilderInitial(t, { newName: `${t.name}_copy` }));
    setShowForm(true);
  };
  const openFix = (t) => {
    setBuilderInitial(toBuilderInitial(t, { rejectedReason: t.rejected_reason || 'Rejected by Meta — rewrite to comply.' }));
    setShowForm(true);
  };
  const openEdit = (t) => {
    setBuilderInitial({ ...toBuilderInitial(t), editId: t.id });
    setShowForm(true);
  };
  const onCreated = () => { setShowForm(false); setBuilderInitial(null); load(); };

  const [analyticsFor, setAnalyticsFor] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const openAnalytics = (t) => {
    if (analyticsFor === t.name) { setAnalyticsFor(''); return; }
    setAnalyticsFor(t.name);
    setAnalytics(null);
    setAnalyticsLoading(true);
    api.get(`/api/social-connectors/whatsapp/templates/${encodeURIComponent(t.name)}/analytics`)
      .then((r) => setAnalytics(r.data)).catch(() => toast.error('Could not load analytics'))
      .finally(() => setAnalyticsLoading(false));
  };

  const sendTemplate = async () => {
    if (!sendLeadId) { toast.error('Pick a lead to send to'); return; }
    const needed = getBodyVarCount(sendFor);
    const map = sendMap.slice(0, needed);
    if (needed > 0 && map.some((m) => m.field === 'custom' && !(m.value || '').trim())) {
      toast.error('Fill every "Custom text" value before sending');
      return;
    }
    try { localStorage.setItem(mapKey(sendFor.name), JSON.stringify(map)); } catch { /* ignore */ }
    const param_map = map.map((m) => (m.field === 'custom'
      ? { field: 'custom', value: (m.value || '').trim() }
      : { field: m.field }));
    setSending(true);
    try {
      const res = await api.post('/api/social-connectors/whatsapp/send-template', {
        lead_id: sendLeadId, template_name: sendFor.name,
        language: sendFor.language || 'en_US', category: sendFor.category, param_map,
      });
      if (res.data?.success) {
        toast.success(`Template "${sendFor.name}" sent via WhatsApp API`);
        setSendFor(null); setSendLeadId(''); setSendMap([]);
      } else {
        toast.error('Send failed', { description: res.data?.detail || 'Provider rejected the send' });
      }
    } catch (e) {
      toast.error('Send failed', { description: e.response?.data?.detail || 'Could not send template' });
    } finally {
      setSending(false);
    }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/api/social-connectors/whatsapp/templates/${encodeURIComponent(name)}`);
      toast.success('Template deleted');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail) {
        toast.error('Template in use', { description: detail, duration: 8000 });
      } else {
        toast.error('Delete failed', { description: typeof detail === 'string' ? detail : undefined });
      }
    }
  };

  const selectedLead = leads.find((l) => l.id === sendLeadId);
  const previewValue = (field) => {
    if (!selectedLead) return '';
    const enc = (v) => (v && !String(v).startsWith('ENC::') ? v : '(from lead record)');
    switch (field) {
      case 'first_name': return (selectedLead.name || '').trim().split(/\s+/)[0] || '—';
      case 'full_name': return selectedLead.name || '—';
      case 'company': return selectedLead.company || '—';
      case 'city': return selectedLead.city || '—';
      case 'title': return selectedLead.title || '—';
      case 'country': return selectedLead.country || '—';
      case 'email': return enc(selectedLead.email);
      case 'phone': return enc(selectedLead.phone);
      default: return '';
    }
  };
  const setVarField = (i, field) => setSendMap((prev) => { const n = [...prev]; n[i] = { ...(n[i] || {}), field }; return n; });
  const setVarValue = (i, value) => setSendMap((prev) => { const n = [...prev]; n[i] = { ...(n[i] || {}), value }; return n; });

  // Phase 4 — status buckets + counts (Action Required = REJECTED or PAUSED).
  const bucketOf = (s) => {
    const u = (s || '').toUpperCase();
    if (u === 'APPROVED') return 'APPROVED';
    if (u === 'PENDING_DELETION' || u === 'DELETED') return 'DELETED';
    if (u === 'PENDING' || u === 'IN_APPEAL') return 'PENDING';
    if (u === 'REJECTED') return 'REJECTED';
    if (u === 'PAUSED' || u === 'DISABLED' || u === 'FLAGGED') return 'PAUSED';
    return 'OTHER';
  };
  const counts = templates.reduce((acc, t) => { const b = bucketOf(t.status); acc[b] = (acc[b] || 0) + 1; acc.ALL += 1; return acc; }, { ALL: 0 });
  const actionRequired = (counts.REJECTED || 0) + (counts.PAUSED || 0);
  const TABS = [
    { key: 'ALL', label: 'All', n: counts.ALL || 0 },
    { key: 'APPROVED', label: 'Approved', n: counts.APPROVED || 0 },
    { key: 'PENDING', label: 'Pending', n: counts.PENDING || 0 },
    { key: 'ACTION', label: 'Action Required', n: actionRequired },
    { key: 'DELETED', label: 'Deleted on Meta', n: counts.DELETED || 0 },
  ];
  const visibleTemplates = templates.filter((t) => {
    if (statusTab === 'ALL') return true;
    if (statusTab === 'ACTION') return ['REJECTED', 'PAUSED'].includes(bucketOf(t.status));
    return bucketOf(t.status) === statusTab;
  });
  const existingNames = new Set(templates.map((t) => (t.name || '').toLowerCase()));

  // Quality rating → GREEN/YELLOW/RED badge.
  const QUALITY = {
    GREEN: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    YELLOW: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    RED: 'bg-red-500/10 text-red-600 border-red-500/20',
  };

  return (
    <div className="space-y-3" data-testid="whatsapp-templates-panel">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="wa-templates-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Sync Status
        </Button>
        <Button size="sm" onClick={() => (showForm ? onCreated() : openNew())} data-testid="wa-templates-new-btn">
          <Plus className="h-4 w-4 mr-2" />{showForm ? 'Close' : 'New Template'}
        </Button>
      </div>

      {!notConnected && templates.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="wa-status-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setStatusTab(tb.key)}
              className={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${statusTab === tb.key ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:text-foreground'} ${tb.key === 'ACTION' && tb.n > 0 && statusTab !== tb.key ? 'text-red-600 border-red-500/30' : ''}`}
              data-testid={`wa-status-tab-${tb.key.toLowerCase()}`}
            >
              {tb.label} <span className="opacity-70">{tb.n}</span>
            </button>
          ))}
        </div>
      )}

      {notice && <p className="text-xs text-amber-600 bg-amber-500/10 rounded-md p-2" data-testid="wa-templates-notice">{notice}</p>}

      {notConnected && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-center" data-testid="wa-templates-not-connected">
          <p className="text-sm font-medium">WhatsApp isn't connected yet</p>
          <p className="text-xs text-muted-foreground mt-1">Connect your WhatsApp Business number to view and manage approved message templates.</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => { window.location.href = '/whatsapp?tab=connection'; }} data-testid="wa-templates-connect-cta">Go to Connection</Button>
        </div>
      )}

      {showForm && (
        <WhatsAppTemplateBuilder
          key={builderInitial ? (builderInitial.editId || builderInitial.form.name + builderInitial.form.language) : 'new'}
          initial={builderInitial}
          existingNames={existingNames}
          onCreated={onCreated}
          onCancel={() => { setShowForm(false); setBuilderInitial(null); }}
        />
      )}

      <div className="space-y-2" data-testid="wa-templates-list">
        {notConnected ? null : loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No templates yet. Create your first one above.</p>
        ) : visibleTemplates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="wa-templates-empty-tab">No templates in this status.</p>
        ) : (
          visibleTemplates.map((t) => (
            <div key={t.id || t.name} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border" data-testid={`wa-template-${t.name}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{t.name}</span>
                  <span className={`text-[10px] rounded border px-1.5 py-0.5 ${STATUS_COLORS[up(t.status)] || ''}`} data-testid={`wa-template-status-${t.name}`}>{statusLabel(t.status)}</span>
                  {t.pending_sync && (
                    <span className="text-[10px] rounded border px-1.5 py-0.5 bg-sky-500/10 text-sky-600 border-sky-500/20 flex items-center gap-0.5" data-testid={`wa-template-syncing-${t.name}`} title="This template exists on Meta but hasn't synced back to the app yet — it's in review, not broken.">
                      <RefreshCw className="h-2.5 w-2.5 animate-spin" />Syncing with Meta
                    </span>
                  )}
                  <span className="text-[10px] rounded border px-1.5 py-0.5">{t.category}</span>
                  <span className="text-[10px] text-muted-foreground">{t.language}</span>
                  {fmtCreated(t.created_at) && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5" data-testid={`wa-template-created-${t.name}`}>
                      <Clock className="h-2.5 w-2.5" />Created {fmtCreated(t.created_at)}
                    </span>
                  )}
                  {t.quality && (
                    <span className={`text-[10px] rounded border px-1.5 py-0.5 ${QUALITY[(t.quality || '').toUpperCase()] || ''}`} data-testid={`wa-template-quality-badge-${t.name}`}>
                      ● {t.quality}
                    </span>
                  )}
                </div>
                {t.components?.filter((c) => c.text).map((c, i) => (
                  <p key={i} className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.text}</p>
                ))}
                {getComp(t, 'BUTTONS') && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`wa-template-buttons-${t.name}`}>
                    {(getComp(t, 'BUTTONS').buttons || []).map((b, i) => (
                      <span key={i} className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-600">{b.text || b.type}</span>
                    ))}
                  </div>
                )}
                {['PENDING_DELETION', 'DELETED'].includes(up(t.status)) ? (
                  <p className="mt-1 flex items-start gap-1 text-[11px] text-red-600" data-testid={`wa-template-deleted-${t.name}`}>
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    Deleted on Meta — this is usually done by another app or partner connected to the same WhatsApp Business Account (e.g. <span className="font-medium">AiSensy</span>). The name may be unavailable to reuse for ~30 days. Check WhatsApp Manager → connected partners if this wasn&apos;t you.
                  </p>
                ) : (t.rejected_reason || ['REJECTED', 'PAUSED', 'DISABLED', 'FLAGGED'].includes(up(t.status))) && (
                  <p className="mt-1 flex items-start gap-1 text-[11px] text-red-600" data-testid={`wa-template-reason-${t.name}`}>
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    {t.rejected_reason || (up(t.status) === 'PAUSED'
                      ? 'Paused by Meta — usually low quality or a policy issue. Edit and improve the copy to re-enable it.'
                      : 'Action required — Meta flagged this template. Open Edit to review and resubmit.')}
                  </p>
                )}

                <div className="mt-2 flex flex-wrap gap-2">
                  {t.status === 'REJECTED' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600" onClick={() => openFix(t)} data-testid={`wa-fix-resubmit-${t.name}`}>
                      <Wand2 className="h-3 w-3 mr-1" />Fix & resubmit
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openDuplicate(t)} data-testid={`wa-duplicate-${t.name}`}>
                    <Copy className="h-3 w-3 mr-1" />Duplicate / new language
                  </Button>
                  {['APPROVED', 'REJECTED', 'PAUSED'].includes(t.status) && t.id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openEdit(t)} data-testid={`wa-edit-${t.name}`}>
                      <Pencil className="h-3 w-3 mr-1" />Edit
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openAnalytics(t)} data-testid={`wa-analytics-btn-${t.name}`}>
                    <BarChart3 className="h-3 w-3 mr-1" />Analytics
                  </Button>
                </div>

                {analyticsFor === t.name && (
                  <div className="mt-2 rounded-md border border-border bg-background p-2" data-testid={`wa-analytics-panel-${t.name}`}>
                    {analyticsLoading ? (
                      <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                    ) : analytics ? (
                      <>
                        <div className="grid grid-cols-5 gap-1 text-center">
                          {[
                            { k: 'sent', label: 'Sent', v: analytics.sent },
                            { k: 'delivered', label: 'Delivered', v: analytics.delivered, r: analytics.delivered_rate },
                            { k: 'read', label: 'Read', v: analytics.read, r: analytics.read_rate },
                            { k: 'replied', label: 'Replied', v: analytics.replied, r: analytics.replied_rate },
                            { k: 'failed', label: 'Failed', v: analytics.failed },
                          ].map((m) => (
                            <div key={m.k} className="rounded bg-muted/40 py-1.5" data-testid={`wa-analytics-${m.k}-${t.name}`}>
                              <p className={`text-sm font-semibold ${m.k === 'failed' && m.v > 0 ? 'text-red-600' : ''}`}>{m.v}</p>
                              <p className="text-[9px] text-muted-foreground">{m.label}{m.r != null ? ` · ${m.r}%` : ''}</p>
                            </div>
                          ))}
                        </div>
                        {analytics.withheld_131049 > 0 && (
                          <p className="mt-1.5 flex items-start gap-1 text-[10px] text-amber-600" data-testid={`wa-analytics-131049-${t.name}`}>
                            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{analytics.withheld_131049} marketing message(s) were withheld by Meta (131049 — per-user marketing limit). For transactional content, use a UTILITY template so it always lands.
                          </p>
                        )}
                        {analytics.sent === 0 && <p className="mt-1 text-[10px] text-muted-foreground">No sends yet for this template.</p>}
                      </>
                    ) : null}
                  </div>
                )}

                {t.status === 'APPROVED' && (
                  sendFor?.name === t.name ? (
                    <div className="mt-2 space-y-2 p-2 rounded-md border border-green-500/30 bg-green-500/5" data-testid="wa-send-panel">
                      <Select value={sendLeadId} onValueChange={setSendLeadId}>
                        <SelectTrigger className="h-8 text-xs" data-testid="wa-send-lead"><SelectValue placeholder="Select a lead…" /></SelectTrigger>
                        <SelectContent>
                          {leads.filter((l) => l.phone).map((l) => (
                            <SelectItem key={l.id} value={l.id}>{l.name || l.phone} · {l.phone}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getBodyVarCount(t) > 0 ? (
                        <div className="space-y-2" data-testid="wa-send-vars">
                          <p className="text-[10px] text-muted-foreground">Variables auto-fill from the lead — pick a field for each, or type custom text.</p>
                          {Array.from({ length: getBodyVarCount(t) }).map((_, i) => {
                            const m = sendMap[i] || { field: 'custom', value: '' };
                            return (
                              <div key={i} className="flex items-center gap-2" data-testid={`wa-send-var-${i + 1}`}>
                                <span className="text-[10px] font-mono text-muted-foreground w-8 shrink-0">{`{{${i + 1}}}`}</span>
                                <Select value={m.field} onValueChange={(v) => setVarField(i, v)}>
                                  <SelectTrigger className="h-8 text-xs w-32 shrink-0" data-testid={`wa-send-field-${i + 1}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{FIELD_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}</SelectContent>
                                </Select>
                                {m.field === 'custom' ? (
                                  <Input className="h-8 text-xs flex-1" placeholder="Type value…" value={m.value || ''} onChange={(e) => setVarValue(i, e.target.value)} data-testid={`wa-send-custom-${i + 1}`} />
                                ) : (
                                  <span className="text-xs flex-1 truncate text-foreground/80 px-2 py-1 rounded bg-muted/40" data-testid={`wa-send-preview-${i + 1}`}>
                                    {sendLeadId ? previewValue(m.field) : FIELD_LABEL[m.field]}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[10px] text-muted-foreground" data-testid="wa-send-novars">No variables — this template sends as-is.</p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs flex-1" onClick={sendTemplate} disabled={sending} data-testid="wa-send-confirm">
                          {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}Send via API
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSendFor(null)} data-testid="wa-send-cancel">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs mt-2" onClick={() => { setSendFor(t); setSendLeadId(''); setSendMap(loadSavedMap(t)); }} data-testid={`wa-send-btn-${t.name}`}>
                      <Send className="h-3 w-3 mr-1" />Send via API
                    </Button>
                  )
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 shrink-0" onClick={() => remove(t.name)} data-testid={`wa-template-delete-${t.name}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WhatsAppTemplatesPanel;
